import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Percent, Save } from "lucide-react";
import { billingApi } from "./api";
import { useToast } from "@/app/providers/Toaster";

/**
 * Comisión de Consumos (#23) — parámetro editable de la tasa que Mixcore cobra
 * sobre el bruto de la venta de consumos (default 4%, excl. MP). La UI trabaja
 * en PORCENTAJE (ej. 4 = 4%); el backend guarda el decimal (0.04). Tope 50%.
 *
 * Esta tasa solo se aplica cuando la feature-flag CONSUMO_CHECKOUT está ON
 * (Operations → Features). Con la flag OFF no se cobra nada de consumos.
 */
export function ConsumoCommissionCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { push } = useToast();

  const settingsQuery = useQuery({
    queryKey: ["billing", "settings"],
    queryFn: billingApi.getSettings,
  });

  const [percent, setPercent] = useState<string>("");

  useEffect(() => {
    if (settingsQuery.data) {
      setPercent((settingsQuery.data.consumoCommissionRate * 100).toString());
    }
  }, [settingsQuery.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: (rate: number) => billingApi.updateSettings({ consumoCommissionRate: rate }),
    onSuccess: () => {
      push({ kind: "success", message: "Comisión de consumos guardada." });
      qc.invalidateQueries({ queryKey: ["billing", "settings"] });
    },
    onError: () => push({ kind: "error", message: "No se pudo guardar la comisión." }),
  });

  const current = settingsQuery.data?.consumoCommissionRate ?? null;
  const parsed = Number(percent);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 50;
  const dirty = current != null && valid && parsed / 100 !== current;

  return (
    <div className="border border-border-subtle rounded-lg p-4 bg-bg-subtle/40">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-fg font-semibold">
            <Percent size={15} className="text-fg-muted" />
            Comisión de consumos (#23)
          </div>
          <p className="text-2xs text-fg-subtle mt-1 max-w-xl">
            % que Mixcore cobra sobre el bruto de la venta de consumos (neto, no incluye MercadoPago).
            Se aplica solo cuando el <strong>Pago de Consumos</strong> está activado
            (Operations → Features). Default 4%. Tope 50%.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <label className="text-2xs text-fg-subtle mb-1">Comisión</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                className="nx-input w-24 text-right"
                value={percent}
                min={0}
                max={50}
                step={0.5}
                disabled={!canEdit || settingsQuery.isLoading}
                onChange={(e) => setPercent(e.target.value)}
              />
              <span className="text-fg-muted text-sm">%</span>
            </div>
          </div>
          {canEdit && (
            <button
              className="nx-btn-primary"
              disabled={!dirty || !valid || save.isPending}
              onClick={() => save.mutate(parsed / 100)}
            >
              <Save size={13} /> Guardar
            </button>
          )}
        </div>
      </div>
      {!valid && percent !== "" && (
        <p className="text-2xs text-red-400 mt-2">La comisión debe estar entre 0% y 50%.</p>
      )}
    </div>
  );
}
